import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import Seo from "@/components/base/Seo";
import SectionHeading from "@/components/feature/SectionHeading";
import FeatureGrid from "@/components/feature/FeatureGrid";
import ProcessSteps from "@/components/feature/ProcessSteps";
import FaqAccordion from "@/components/feature/FaqAccordion";
import FinalCta from "@/pages/home/components/FinalCta";
import { whatsappLink } from "@/constants/site";
import { systemTypes, systemBenefits, systemSteps, sistemasFaqs } from "@/mocks/sistemas";

const dashboardRows = [
  { label: "Pedidos processados", value: "1.248", trend: "+12%" },
  { label: "Clientes ativos", value: "3.907", trend: "+8%" },
  { label: "Tarefas automatizadas", value: "18.540", trend: "+21%" },
];

const bars = [38, 52, 46, 64, 58, 74, 69, 86];

export default function Sistemas() {
  return (
    <SiteLayout>
      <Seo
        title="Sistemas Web e Soluções Personalizadas para Empresas | NXHOST"
        description="Desenvolvemos sistemas web personalizados: painéis, dashboards, CRM, portais e integrações. Soluções digitais sob medida para a sua empresa com a NXHOST."
        keywords="sistemas web, sistema personalizado, dashboard, CRM, portal, integração de sistemas, automação"
        path="/sistemas"
        titleEn="Custom Web Systems and Solutions for Companies | NXHOST"
        titleEs="Sistemas Web y Soluciones Personalizadas para Empresas | NXHOST"
        descriptionEn="We build custom web systems: panels, dashboards, CRM, portals and integrations. Tailor-made digital solutions for your company with NXHOST."
        descriptionEs="Desarrollamos sistemas web personalizados: paneles, dashboards, CRM, portales e integraciones. Soluciones digitales a medida para tu empresa con NXHOST."
        breadcrumbs={[
          { name: "Início", path: "/" },
          { name: "Sistemas", path: "/sistemas" },
        ]}
        faqs={sistemasFaqs}
      />

      <PageHero
        badge="Sistemas web"
        badgeIcon="ri-code-s-slash-line"
        title="Sistemas web sob medida para a sua operação."
        subtitle="Quando a sua empresa precisa de uma solução específica, criamos sistemas web personalizados para organizar processos, dados e atendimentos em um só lugar."
        crumbs={[{ label: "Início", to: "/" }, { label: "Sistemas" }]}
      >
        <Reveal delay={220}>
          <div className="rounded-2xl border border-background-50/10 bg-foreground-900/60 p-5 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-background-400">
                Painel do sistema
              </span>
              <span className="flex items-center gap-1.5 text-[11px] font-medium text-secondary-400">
                <span className="h-2 w-2 rounded-full bg-secondary-400"></span>
                Ao vivo
              </span>
            </div>
            <div className="mt-5 flex h-24 items-end gap-2">
              {bars.map((bar, index) => (
                <div
                  key={index}
                  className="flex-1 rounded-t-sm bg-gradient-to-t from-primary-500 to-accent-400"
                  style={{ height: `${bar}%` }}
                ></div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-background-50/10 pt-4">
              <div>
                <p className="font-heading text-xl font-bold text-background-50">Dados</p>
                <p className="text-[11px] text-background-300">centralizados e organizados</p>
              </div>
              <i className="ri-bar-chart-2-line text-2xl text-accent-300" aria-hidden="true"></i>
            </div>
          </div>
        </Reveal>
      </PageHero>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Tipos de sistema"
            badgeIcon="ri-apps-line"
            title="Soluções digitais sob medida para o seu negócio."
            description="Desenvolvemos sistemas web para diferentes necessidades, sempre alinhados à forma como a sua empresa trabalha."
          />
          <div className="mt-12">
            <FeatureGrid items={systemTypes} columns={4} accent="primary" />
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-accent-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-accent-700">
                  <i className="ri-dashboard-3-line" aria-hidden="true"></i>
                  Painéis e resultados
                </span>
                <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
                  Informação clara para decidir com segurança.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-foreground-600">
                  Reunimos os dados da sua operação em painéis fáceis de entender, com indicadores que
                  ajudam a acompanhar o dia a dia e a tomar decisões melhores.
                </p>

                <div className="mt-8 overflow-hidden rounded-xl border border-background-200 bg-background-100">
                  {dashboardRows.map((row, index) => (
                    <div
                      key={row.label}
                      className={`flex items-center justify-between gap-4 px-5 py-4 ${
                        index < dashboardRows.length - 1 ? "border-b border-background-200" : ""
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-primary-600">
                          <i className="ri-line-chart-line text-lg" aria-hidden="true"></i>
                        </span>
                        <p className="text-sm text-foreground-700">{row.label}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-heading text-sm font-semibold text-foreground-950">
                          {row.value}
                        </span>
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-700">
                          {row.trend}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-foreground-500">
                  Indicadores ilustrativos de como a informação fica organizada no sistema.
                </p>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="overflow-hidden rounded-2xl border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20software%20dashboard%20interface%20with%20charts%20and%20data%20panels%20displayed%20on%20a%20large%20monitor%2C%20dark%20blue%20and%20cyan%20accents%2C%20clean%20professional%20tech%20workspace&width=1100&height=850&seq=nh-sistemas-dashboard&orientation=landscape"
                  alt="Sistema web personalizado com painel de dados e gráficos exibido em um monitor"
                  title="Sistemas web personalizados NXHOST"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Benefícios"
            badgeIcon="ri-shield-star-line"
            badgeTone="secondary"
            title="Um sistema que trabalha a favor da sua empresa."
            description="Automação, informação centralizada e segurança para a sua equipe produzir mais."
          />
          <div className="mt-12">
            <FeatureGrid items={systemBenefits} columns={3} accent="secondary" />
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Como funciona"
            badgeIcon="ri-route-line"
            badgeTone="accent"
            title="Do levantamento à evolução do sistema."
          />
          <div className="mt-12">
            <ProcessSteps steps={systemSteps} />
          </div>

          <Reveal>
            <div className="mt-10 flex flex-col items-center justify-between gap-4 rounded-xl border border-background-200 bg-background-100 p-6 text-center sm:flex-row sm:text-left">
              <div>
                <p className="font-heading text-base font-semibold text-foreground-950">
                  Tem uma necessidade específica?
                </p>
                <p className="mt-1 text-sm text-foreground-600">
                  Conte como funciona a sua operação e desenhamos a solução ideal.
                </p>
              </div>
              <a
                href={whatsappLink("Ola! Preciso de um sistema web personalizado da NXHOST.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-customer-service-2-line text-lg" aria-hidden="true"></i>
                Falar com um especialista
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Dúvidas frequentes"
            badgeIcon="ri-question-line"
            title="Perguntas sobre sistemas web."
          />
          <div className="mt-12">
            <FaqAccordion items={sistemasFaqs} />
          </div>
        </div>
      </section>

      <FinalCta />
    </SiteLayout>
  );
}